import {
  Typography,
  IconButton,
  Divider,
  Tooltip,
  Box,
  Avatar,
  CardHeader,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import Skeleton from "@mui/material/Skeleton";
import SearchIcon from "@mui/icons-material/Search";
import Person from "@mui/icons-material/Person";
import CourseList from "@nualang/nualang-ui-components/dist/Lists/Courses";
import ClassroomList from "@nualang/nualang-ui-components/dist/Lists/Classrooms";
import ResponsiveTabs from "@nualang/nualang-ui-components/dist/Navigation/ResponsiveTabs";
import NualangVerifiedIcon from "@nualang/nualang-ui-components/dist/Misc/NualangVerifiedIcon/NualangVerifiedIcon";
import MedalSelector from "@nualang/nualang-ui-components/dist/Misc/MedalSelector/MedalSelector";

const useStyles = makeStyles()((theme) => ({
  root: {
    height: "100%",
    padding: theme.spacing(1, 2),
    [theme.breakpoints.up("sm")]: {
      padding: theme.spacing(2, 3),
    },
  },
  cardHeader: {
    padding: theme.spacing(2, 0),
  },
  cardTopAvatar: {
    [theme.breakpoints.down("md")]: {
      width: "60px",
      height: "60px",
    },
    width: "80px",
    height: "80px",
    border: `3px solid ${theme.palette.background.paper}`,
    backgroundColor: `${theme.palette.background.paper}`,
    color: theme.palette.text.secondary,
  },
  header: {
    display: "flex",
    alignItems: "center",
    paddingBottom: theme.spacing(2),
  },
  headerText: {
    flexGrow: 1,
  },
}));

export default function Profile({
  t = (text) => text,
  handleJoinClassroom,
  completions = [],
  avatars = [],
  singleLineGrid = true,
  singleLineGridCols = 4,
  handleStartCourse,
  handleLeaveCourse,
  handleDeleteCourse,
  handleViewCourse,
  handleViewCourseTopic,
  handleViewUserProfile,
  membersProps,
  getMemberDetails,
  handleSearch,
  memberPlaceholderImageUrl,
  getCourseSections,
  getCourseMember,
  getCourseMembers,
  memberId,
  username,
  courses = [],
  classrooms = [],
  isLoading,
  profilePicture,
  profileTitle,
  profileSubheader,
  handleLeaveClassroom,
  handleViewClassroom,
  getClassroomMember,
  getClassroomMembers,
  handleSearchClassrooms,
  handleDuplicateCourse,
  verificationStatus,
  gameAward,
}) {
  const { classes } = useStyles();

  const tabs = [
    {
      label: t("home"),
      id: "Home",
    },
    {
      label: `${t("courses")} (${courses.length})`,
      id: "Courses",
    },
    {
      label: `${t("classrooms")} (${classrooms.length})`,
      id: "Classrooms",
    },
  ];

  let initialTabValue;
  if (window.location.hash) {
    const selectedTab = window.location.hash;
    initialTabValue = tabs.findIndex(
      (tab) => tab.id === selectedTab.replace("#", ""),
    );
  } else {
    initialTabValue = 0;
  }

  return (
    <div className={classes.root}>
      <CardHeader
        className={classes.cardHeader}
        avatar={
          <div
            style={{
              position: "relative",
              display: "inline-block",
            }}
          >
            {profilePicture ? (
              <Avatar
                src={profilePicture}
                className={classes.cardTopAvatar}
                alt=''
              />
            ) : (
              <Avatar
                className={classes.cardTopAvatar}
                sx={(theme) => ({
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#ffffff" : "#bdbdbd",
                  color:
                    theme.palette.mode === "dark"
                      ? theme.palette.background.default
                      : "white",
                })}
                alt=''
              >
                <Person fontSize='large' />
              </Avatar>
            )}

            {verificationStatus === "approved" && (
              <NualangVerifiedIcon
                sx={{
                  bottom: "9px",
                  right: "-1px",
                  fontSize: 27,
                }}
              />
            )}
            {gameAward === "gold" && verificationStatus !== "approved" && (
              <MedalSelector
                color={"gold"}
                width={27}
                fade={false}
                backgroundColor={true}
                sx={{
                  position: "absolute",
                  padding: "2px",
                  bottom: "9px",
                  right: "-1px",
                  borderRadius: "50%",
                }}
              />
            )}
            {gameAward === "silver" && verificationStatus !== "approved" && (
              <MedalSelector
                color={"silver"}
                width={27}
                fade={false}
                backgroundColor={true}
                sx={{
                  position: "absolute",
                  padding: "2px",
                  bottom: "9px",
                  right: "-1px",
                  borderRadius: "50%",
                }}
              />
            )}
            {gameAward === "bronze" && verificationStatus !== "approved" && (
              <MedalSelector
                color={"bronze"}
                width={27}
                fade={false}
                backgroundColor={true}
                sx={{
                  position: "absolute",
                  padding: "2px",
                  bottom: "9px",
                  right: "-1px",
                  borderRadius: "50%",
                }}
              />
            )}
          </div>
        }
        title={
          <Typography variant='h5' component='h2'>
            {isLoading ? (
              <Skeleton variant='rectangular' height={32} width={150} />
            ) : (
              <Box
                sx={{
                  fontWeight: 'fontWeightBold',
                  mb: 0.5
                }}>
                {profileTitle}
              </Box>
            )}
          </Typography>
        }
        subheader={isLoading ? "" : `${profileSubheader || ""}`}
      />
      <ResponsiveTabs
        t={t}
        tabs={tabs}
        scrollable
        initialValue={initialTabValue}
      >
        <Box sx={{
          py: 1
        }}>
          {isLoading || courses.length > 0 ? (
            <>
              <Box sx={{
                pb: 2
              }}>
                <div className={classes.header}>
                  <div className={classes.headerText}>
                    <Typography variant='h5' component='h2'>
                      {courses.length > 0 ? (
                        t("courses")
                      ) : (
                        <Skeleton
                          variant='rectangular'
                          height={32}
                          width={250}
                        />
                      )}
                    </Typography>
                  </div>
                  {handleSearch && (
                    <Tooltip title={t("find_course")}>
                      <IconButton
                        onClick={handleSearch}
                        aria-label={t("find_course")}
                        color='inherit'
                        data-cy='search-course-fab'
                        size='large'
                        role='link'
                      >
                        <SearchIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
                <CourseList
                  t={t}
                  memberPlaceholderImageUrl={memberPlaceholderImageUrl}
                  courses={
                    Array.isArray(courses) && courses.length ? courses : null
                  }
                  completions={completions}
                  handleStartCourse={handleStartCourse}
                  handleLeaveCourse={handleLeaveCourse}
                  handleDeleteCourse={handleDeleteCourse}
                  handleViewCourseTopic={handleViewCourseTopic}
                  handleViewCourse={handleViewCourse}
                  handleViewUserProfile={handleViewUserProfile}
                  membersProps={membersProps}
                  getMemberDetails={getMemberDetails}
                  singleLineGrid={singleLineGrid}
                  singleLineGridCols={singleLineGridCols}
                  getCourseSections={getCourseSections}
                  getCourseMember={getCourseMember}
                  getCourseMembers={getCourseMembers}
                  memberId={memberId}
                  username={username}
                  handleDuplicateCourse={handleDuplicateCourse}
                />
              </Box>
              <Box sx={{
                pb: 2
              }}>
                <Divider />
              </Box>
            </>
          ) : null}
          {isLoading || classrooms.length > 0 ? (
            <>
              <Box sx={{
                pb: 2
              }}>
                <div className={classes.header}>
                  <div className={classes.headerText}>
                    <Typography variant='h5' component='h2'>
                      {classrooms.length > 0 ? (
                        t("classrooms")
                      ) : (
                        <Skeleton
                          variant='rectangular'
                          height={32}
                          width={250}
                        />
                      )}
                    </Typography>
                  </div>
                  {handleSearchClassrooms && (
                    <Tooltip title={t("find_classroom")}>
                      <IconButton
                        onClick={handleSearchClassrooms}
                        aria-label={t("find_classroom")}
                        color='inherit'
                        data-cy='search-classroom-fab'
                        size='large'
                      >
                        <SearchIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
                <ClassroomList
                  t={t}
                  memberPlaceholderImageUrl={memberPlaceholderImageUrl}
                  classrooms={
                    Array.isArray(classrooms) && classrooms.length
                      ? classrooms
                      : null
                  }
                  handleJoinClassroom={handleJoinClassroom}
                  handleLeaveClassroom={handleLeaveClassroom}
                  handleViewClassroom={handleViewClassroom}
                  membersProps={membersProps}
                  getMemberDetails={getMemberDetails}
                  singleLineGrid={singleLineGrid}
                  singleLineGridCols={singleLineGridCols}
                  getClassroomMember={getClassroomMember}
                  getClassroomMembers={getClassroomMembers}
                  username={username}
                  handleDuplicateCourse={handleDuplicateCourse}
                />
              </Box>
              <Box sx={{
                pb: 2
              }}>
                <Divider />
              </Box>
            </>
          ) : null}
          {!isLoading &&
            courses.length === 0 &&
            classrooms.length === 0 && (
              <Typography>{t("no_public_content_created")}</Typography>
            )}
        </Box>
        <Box sx={{
          py: 1
        }}>
          {isLoading || courses.length > 0 ? (
            <>
              <Box sx={{
                pb: 2
              }}>
                <div className={classes.header}>
                  <div className={classes.headerText}>
                    <Typography variant='h5' component='h2'>
                      {courses.length > 0 ? (
                        t("courses")
                      ) : (
                        <Skeleton
                          variant='rectangular'
                          height={32}
                          width={250}
                        />
                      )}
                    </Typography>
                  </div>
                  {handleSearch && (
                    <Tooltip title={t("find_course")}>
                      <IconButton
                        onClick={handleSearch}
                        aria-label={t("find_course")}
                        color='inherit'
                        data-cy='search-course-fab'
                        size='large'
                      >
                        <SearchIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
                <CourseList
                  t={t}
                  memberPlaceholderImageUrl={memberPlaceholderImageUrl}
                  courses={
                    Array.isArray(courses) && courses.length ? courses : null
                  }
                  completions={completions}
                  handleStartCourse={handleStartCourse}
                  handleLeaveCourse={handleLeaveCourse}
                  handleDeleteCourse={handleDeleteCourse}
                  handleViewCourseTopic={handleViewCourseTopic}
                  handleViewCourse={handleViewCourse}
                  handleViewUserProfile={handleViewUserProfile}
                  membersProps={membersProps}
                  getMemberDetails={getMemberDetails}
                  avatars={avatars}
                  getCourseSections={getCourseSections}
                  getCourseMember={getCourseMember}
                  getCourseMembers={getCourseMembers}
                  memberId={memberId}
                  username={username}
                  handleDuplicateCourse={handleDuplicateCourse}
                />
              </Box>
              <Box sx={{
                pb: 2
              }}>
                <Divider />
              </Box>
            </>
          ) : (
            <Typography>{t("no_public_content_created")}</Typography>
          )}
        </Box>
        <Box sx={{
          py: 1
        }}>
          {isLoading || classrooms.length > 0 ? (
            <>
              <Box sx={{
                pb: 2
              }}>
                <div className={classes.header}>
                  <div className={classes.headerText}>
                    <Typography variant='h5' component='h2'>
                      {classrooms.length > 0 ? (
                        t("classrooms")
                      ) : (
                        <Skeleton
                          variant='rectangular'
                          height={32}
                          width={250}
                        />
                      )}
                    </Typography>
                  </div>
                  {handleSearchClassrooms && (
                    <Tooltip title={t("find_classroom")}>
                      <IconButton
                        onClick={handleSearchClassrooms}
                        aria-label={t("find_classroom")}
                        color='inherit'
                        data-cy='search-classroom-fab'
                        size='large'
                      >
                        <SearchIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
                <ClassroomList
                  t={t}
                  memberPlaceholderImageUrl={memberPlaceholderImageUrl}
                  classrooms={
                    Array.isArray(classrooms) && classrooms.length
                      ? classrooms
                      : null
                  }
                  handleJoinClassroom={handleJoinClassroom}
                  handleLeaveClassroom={handleLeaveClassroom}
                  handleViewClassroom={handleViewClassroom}
                  membersProps={membersProps}
                  getMemberDetails={getMemberDetails}
                  getClassroomMember={getClassroomMember}
                  getClassroomMembers={getClassroomMembers}
                  username={username}
                  handleDuplicateCourse={handleDuplicateCourse}
                />
              </Box>
              <Box sx={{
                pb: 2
              }}>
                <Divider />
              </Box>
            </>
          ) : (
            <Typography>{t("no_public_content_created")}</Typography>
          )}
        </Box>
      </ResponsiveTabs>
    </div>
  );
}
